import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  FormHelperText,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Security as SecurityIcon,
  Notifications as NotificationIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  AttachMoney as TaxIcon,
  Policy as PolicyIcon,
  Palette as ThemeIcon,
  Language as LanguageIcon,
  CloudUpload as BackupIcon,
  VpnKey as ApiIcon,
  People as UsersIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  RestartAlt as RestartIcon,
  Download as ExportIcon,
  Upload as ImportIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [initialLoad, setInitialLoad] = useState(true);
  
  // Settings state
  const [generalSettings, setGeneralSettings] = useState({
    businessName: 'Mountain View RV Park',
    businessEmail: 'info@mountainviewrv.com',
    businessPhone: '+1-555-0123',
    businessAddress: {
      street: '123 RV Park Road',
      city: 'Denver',
      state: 'CO',
      zipCode: '80202',
      country: 'USA'
    },
    timezone: 'America/Denver',
    currency: 'USD',
    dateFormat: 'MM/DD/YYYY',
    language: 'en'
  });

  const [operatingHours, setOperatingHours] = useState({
    checkInTime: '14:00',
    checkOutTime: '11:00',
    officeHours: {
      monday: { open: '08:00', close: '18:00', closed: false },
      tuesday: { open: '08:00', close: '18:00', closed: false },
      wednesday: { open: '08:00', close: '18:00', closed: false },
      thursday: { open: '08:00', close: '18:00', closed: false },
      friday: { open: '08:00', close: '18:00', closed: false },
      saturday: { open: '09:00', close: '17:00', closed: false },
      sunday: { open: '09:00', close: '17:00', closed: false }
    },
    quietHours: {
      start: '22:00',
      end: '08:00'
    }
  });

  const [paymentSettings, setPaymentSettings] = useState({
    acceptedMethods: ['cash', 'card', 'check'],
    stripeEnabled: true,
    stripePublishableKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    stripeConnectEnabled: false,
    stripePlatformFeePercent: 2.5,
    requireDeposit: true,
    depositAmount: 100,
    depositType: 'fixed', // 'fixed' or 'percentage'
    cancellationFee: 25,
    processingFee: 2.9,
    taxRate: 8.5
  });

  const [emailSettings, setEmailSettings] = useState({
    provider: 'smtp',
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    fromEmail: 'noreply@mountainviewrv.com',
    fromName: 'Mountain View RV Park',
    enableNotifications: true,
    notificationTypes: {
      newReservation: true,
      cancellation: true,
      checkInReminder: true,
      checkOutReminder: true,
      paymentReceived: true,
      guestMessage: true
    }
  });

  const [smsSettings, setSmsSettings] = useState({
    enabled: true,
    provider: 'twilio',
    twilioAccountSid: '',
    twilioAuthToken: '',
    twilioPhoneNumber: '',
    enableNotifications: true,
    notificationTypes: {
      reservationConfirmation: true,
      checkInReminder: true,
      checkOutReminder: false,
      paymentReminder: true
    }
  });

  const [securitySettings, setSecuritySettings] = useState({
    passwordMinLength: 8,
    passwordRequireUppercase: true,
    passwordRequireLowercase: true,
    passwordRequireNumbers: true,
    passwordRequireSpecial: false,
    sessionTimeout: 60, // minutes
    twoFactorEnabled: false,
    ipWhitelist: [],
    failedLoginAttempts: 5,
    lockoutDuration: 30 // minutes
  });

  const [policies, setPolicies] = useState({
    cancellationPolicy: '48 hours notice required for full refund',
    petPolicy: 'Pets allowed with $25 fee per stay',
    checkInAge: 18,
    maxOccupancy: 6,
    maxVehicles: 2,
    additionalRules: [
      'No smoking in any rental units',
      'Quiet hours from 10 PM to 8 AM',
      'Speed limit 5 MPH throughout park'
    ]
  });

  const [integrations, setIntegrations] = useState([
    { name: 'Stripe', status: 'connected', lastSync: '2025-07-01T10:00:00Z' },
    { name: 'Twilio', status: 'connected', lastSync: '2025-07-01T09:45:00Z' },
    { name: 'Google Calendar', status: 'disconnected', lastSync: null },
    { name: 'QuickBooks', status: 'disconnected', lastSync: null }
  ]);

  // Load settings from backend on mount
  useEffect(() => {
    if (initialLoad) {
      loadSettings();
      setInitialLoad(false);
    }
  }, [initialLoad]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load all settings in parallel
      const [general, payment, operatingHours, security] = await Promise.all([
        api.get('/settings/general'),
        api.get('/settings/payment'),
        api.get('/settings/operating-hours'),
        api.get('/settings/security')
      ]);

      if (general.data) {
        setGeneralSettings(general.data);
      }
      if (payment.data) {
        setPaymentSettings({
          ...payment.data,
          stripeSecretKey: '', // Never populate secret key from backend
          stripeWebhookSecret: '' // Never populate webhook secret from backend
        });
      }
      if (operatingHours.data) {
        setOperatingHours(operatingHours.data);
      }
      if (security.data) {
        setSecuritySettings(security.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSaveSettings = async (section: string) => {
    setSaveStatus('saving');
    try {
      let endpoint = '/settings/';
      let data = {};
      
      switch (section) {
        case 'general':
          endpoint += 'general';
          data = generalSettings;
          break;
        case 'operating':
          endpoint += 'operating-hours';
          data = operatingHours;
          break;
        case 'payment':
          endpoint += 'payment';
          data = paymentSettings;
          break;
        case 'email':
          endpoint += 'email';
          data = emailSettings;
          break;
        case 'sms':
          endpoint += 'sms';
          data = smsSettings;
          break;
        case 'security':
          endpoint += 'security';
          data = securitySettings;
          break;
        case 'policies':
          endpoint += 'policies';
          data = policies;
          break;
      }
      
      await api.put(endpoint, data);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const renderGeneralSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Business Information
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Business Name"
          value={generalSettings.businessName}
          onChange={(e) => setGeneralSettings({ ...generalSettings, businessName: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Business Email"
          type="email"
          value={generalSettings.businessEmail}
          onChange={(e) => setGeneralSettings({ ...generalSettings, businessEmail: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Business Phone"
          value={generalSettings.businessPhone}
          onChange={(e) => setGeneralSettings({ ...generalSettings, businessPhone: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Timezone</InputLabel>
          <Select
            value={generalSettings.timezone}
            onChange={(e) => setGeneralSettings({ ...generalSettings, timezone: e.target.value })}
            label="Timezone"
          >
            <MenuItem value="America/New_York">Eastern Time</MenuItem>
            <MenuItem value="America/Chicago">Central Time</MenuItem>
            <MenuItem value="America/Denver">Mountain Time</MenuItem>
            <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Business Address
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Street Address"
          value={generalSettings.businessAddress.street}
          onChange={(e) => setGeneralSettings({
            ...generalSettings,
            businessAddress: { ...generalSettings.businessAddress, street: e.target.value }
          })}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="City"
          value={generalSettings.businessAddress.city}
          onChange={(e) => setGeneralSettings({
            ...generalSettings,
            businessAddress: { ...generalSettings.businessAddress, city: e.target.value }
          })}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="State"
          value={generalSettings.businessAddress.state}
          onChange={(e) => setGeneralSettings({
            ...generalSettings,
            businessAddress: { ...generalSettings.businessAddress, state: e.target.value }
          })}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="ZIP Code"
          value={generalSettings.businessAddress.zipCode}
          onChange={(e) => setGeneralSettings({
            ...generalSettings,
            businessAddress: { ...generalSettings.businessAddress, zipCode: e.target.value }
          })}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Regional Settings
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Currency</InputLabel>
          <Select
            value={generalSettings.currency}
            onChange={(e) => setGeneralSettings({ ...generalSettings, currency: e.target.value })}
            label="Currency"
          >
            <MenuItem value="USD">USD ($)</MenuItem>
            <MenuItem value="CAD">CAD ($)</MenuItem>
            <MenuItem value="EUR">EUR (€)</MenuItem>
            <MenuItem value="GBP">GBP (£)</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Date Format</InputLabel>
          <Select
            value={generalSettings.dateFormat}
            onChange={(e) => setGeneralSettings({ ...generalSettings, dateFormat: e.target.value })}
            label="Date Format"
          >
            <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
            <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
            <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Language</InputLabel>
          <Select
            value={generalSettings.language}
            onChange={(e) => setGeneralSettings({ ...generalSettings, language: e.target.value })}
            label="Language"
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="es">Spanish</MenuItem>
            <MenuItem value="fr">French</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSaveSettings('general')}
            disabled={saveStatus === 'saving'}
          >
            Save General Settings
          </Button>
        </Box>
      </Grid>
    </Grid>
  );

  const renderOperatingHours = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Check-In / Check-Out Times
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Check-In Time"
          type="time"
          value={operatingHours.checkInTime}
          onChange={(e) => setOperatingHours({ ...operatingHours, checkInTime: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Check-Out Time"
          type="time"
          value={operatingHours.checkOutTime}
          onChange={(e) => setOperatingHours({ ...operatingHours, checkOutTime: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Office Hours
        </Typography>
      </Grid>
      {Object.entries(operatingHours.officeHours).map(([day, hours]) => (
        <Grid item xs={12} key={day}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography sx={{ textTransform: 'capitalize' }}>
                  {day}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Open"
                  type="time"
                  value={hours.open}
                  disabled={hours.closed}
                  onChange={(e) => setOperatingHours({
                    ...operatingHours,
                    officeHours: {
                      ...operatingHours.officeHours,
                      [day]: { ...hours, open: e.target.value }
                    }
                  })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Close"
                  type="time"
                  value={hours.close}
                  disabled={hours.closed}
                  onChange={(e) => setOperatingHours({
                    ...operatingHours,
                    officeHours: {
                      ...operatingHours.officeHours,
                      [day]: { ...hours, close: e.target.value }
                    }
                  })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hours.closed}
                      onChange={(e) => setOperatingHours({
                        ...operatingHours,
                        officeHours: {
                          ...operatingHours.officeHours,
                          [day]: { ...hours, closed: e.target.checked }
                        }
                      })}
                    />
                  }
                  label="Closed"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Quiet Hours
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Quiet Hours Start"
          type="time"
          value={operatingHours.quietHours.start}
          onChange={(e) => setOperatingHours({
            ...operatingHours,
            quietHours: { ...operatingHours.quietHours, start: e.target.value }
          })}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Quiet Hours End"
          type="time"
          value={operatingHours.quietHours.end}
          onChange={(e) => setOperatingHours({
            ...operatingHours,
            quietHours: { ...operatingHours.quietHours, end: e.target.value }
          })}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSaveSettings('operating')}
            disabled={saveStatus === 'saving'}
          >
            Save Operating Hours
          </Button>
        </Box>
      </Grid>
    </Grid>
  );

  const renderPaymentSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Payment Methods
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Switch
                checked={paymentSettings.acceptedMethods.includes('cash')}
                onChange={(e) => {
                  const methods = e.target.checked 
                    ? [...paymentSettings.acceptedMethods, 'cash']
                    : paymentSettings.acceptedMethods.filter(m => m !== 'cash');
                  setPaymentSettings({ ...paymentSettings, acceptedMethods: methods });
                }}
              />
            }
            label="Cash"
          />
          <FormControlLabel
            control={
              <Switch
                checked={paymentSettings.acceptedMethods.includes('card')}
                onChange={(e) => {
                  const methods = e.target.checked 
                    ? [...paymentSettings.acceptedMethods, 'card']
                    : paymentSettings.acceptedMethods.filter(m => m !== 'card');
                  setPaymentSettings({ ...paymentSettings, acceptedMethods: methods });
                }}
              />
            }
            label="Credit/Debit Card"
          />
          <FormControlLabel
            control={
              <Switch
                checked={paymentSettings.acceptedMethods.includes('check')}
                onChange={(e) => {
                  const methods = e.target.checked 
                    ? [...paymentSettings.acceptedMethods, 'check']
                    : paymentSettings.acceptedMethods.filter(m => m !== 'check');
                  setPaymentSettings({ ...paymentSettings, acceptedMethods: methods });
                }}
              />
            }
            label="Check"
          />
        </FormGroup>
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Stripe Integration
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={paymentSettings.stripeEnabled}
              onChange={(e) => setPaymentSettings({ ...paymentSettings, stripeEnabled: e.target.checked })}
            />
          }
          label="Enable Stripe Payments"
        />
      </Grid>
      {paymentSettings.stripeEnabled && (
        <>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Stripe Publishable Key"
              value={paymentSettings.stripePublishableKey}
              onChange={(e) => setPaymentSettings({ ...paymentSettings, stripePublishableKey: e.target.value })}
              helperText="Your Stripe publishable key (starts with pk_)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Stripe Secret Key"
              type="password"
              value={paymentSettings.stripeSecretKey}
              onChange={(e) => setPaymentSettings({ ...paymentSettings, stripeSecretKey: e.target.value })}
              helperText="Your Stripe secret key (starts with sk_) - Keep this secure!"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Stripe Webhook Secret"
              type="password"
              value={paymentSettings.stripeWebhookSecret}
              onChange={(e) => setPaymentSettings({ ...paymentSettings, stripeWebhookSecret: e.target.value })}
              helperText="Webhook endpoint secret for verifying Stripe events"
            />
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2">
                Webhook URL: {window.location.origin}/api/billing/webhook?tenant={localStorage.getItem('tenantId') || 'YOUR_TENANT_ID'}
              </Typography>
              <Typography variant="caption">
                Add this URL to your Stripe webhook endpoints with events: payment_intent.succeeded, payment_intent.failed, customer.subscription.updated, customer.subscription.deleted
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                You can also include the tenant ID in the X-Tenant-ID header instead of the query parameter.
              </Typography>
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" gutterBottom>
              Stripe Connect (For Multi-Tenant Billing)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentSettings.stripeConnectEnabled}
                  onChange={(e) => setPaymentSettings({ ...paymentSettings, stripeConnectEnabled: e.target.checked })}
                />
              }
              label="Enable Stripe Connect for platform fees"
            />
          </Grid>
          {paymentSettings.stripeConnectEnabled && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Platform Fee Percentage"
                type="number"
                value={paymentSettings.stripePlatformFeePercent}
                onChange={(e) => setPaymentSettings({ ...paymentSettings, stripePlatformFeePercent: parseFloat(e.target.value) })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                helperText="Percentage of each transaction to collect as platform fee"
              />
            </Grid>
          )}
        </>
      )}
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Fees & Deposits
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={paymentSettings.requireDeposit}
              onChange={(e) => setPaymentSettings({ ...paymentSettings, requireDeposit: e.target.checked })}
            />
          }
          label="Require Deposit"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Deposit Type</InputLabel>
          <Select
            value={paymentSettings.depositType}
            onChange={(e) => setPaymentSettings({ ...paymentSettings, depositType: e.target.value })}
            label="Deposit Type"
            disabled={!paymentSettings.requireDeposit}
          >
            <MenuItem value="fixed">Fixed Amount</MenuItem>
            <MenuItem value="percentage">Percentage</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={paymentSettings.depositType === 'fixed' ? 'Deposit Amount' : 'Deposit Percentage'}
          type="number"
          value={paymentSettings.depositAmount}
          onChange={(e) => setPaymentSettings({ ...paymentSettings, depositAmount: parseFloat(e.target.value) })}
          InputProps={{
            startAdornment: paymentSettings.depositType === 'fixed' ? 
              <InputAdornment position="start">$</InputAdornment> : null,
            endAdornment: paymentSettings.depositType === 'percentage' ? 
              <InputAdornment position="end">%</InputAdornment> : null
          }}
          disabled={!paymentSettings.requireDeposit}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Cancellation Fee"
          type="number"
          value={paymentSettings.cancellationFee}
          onChange={(e) => setPaymentSettings({ ...paymentSettings, cancellationFee: parseFloat(e.target.value) })}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Tax Rate"
          type="number"
          value={paymentSettings.taxRate}
          onChange={(e) => setPaymentSettings({ ...paymentSettings, taxRate: parseFloat(e.target.value) })}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>
          }}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSaveSettings('payment')}
            disabled={saveStatus === 'saving'}
          >
            Save Payment Settings
          </Button>
        </Box>
      </Grid>
    </Grid>
  );

  const renderSecuritySettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Password Requirements
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography gutterBottom>Minimum Password Length</Typography>
        <Slider
          value={securitySettings.passwordMinLength}
          onChange={(e, value) => setSecuritySettings({ ...securitySettings, passwordMinLength: value as number })}
          min={6}
          max={20}
          marks
          valueLabelDisplay="auto"
        />
      </Grid>
      <Grid item xs={12}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={securitySettings.passwordRequireUppercase}
                onChange={(e) => setSecuritySettings({ ...securitySettings, passwordRequireUppercase: e.target.checked })}
              />
            }
            label="Require uppercase letters"
          />
          <FormControlLabel
            control={
              <Switch
                checked={securitySettings.passwordRequireLowercase}
                onChange={(e) => setSecuritySettings({ ...securitySettings, passwordRequireLowercase: e.target.checked })}
              />
            }
            label="Require lowercase letters"
          />
          <FormControlLabel
            control={
              <Switch
                checked={securitySettings.passwordRequireNumbers}
                onChange={(e) => setSecuritySettings({ ...securitySettings, passwordRequireNumbers: e.target.checked })}
              />
            }
            label="Require numbers"
          />
          <FormControlLabel
            control={
              <Switch
                checked={securitySettings.passwordRequireSpecial}
                onChange={(e) => setSecuritySettings({ ...securitySettings, passwordRequireSpecial: e.target.checked })}
              />
            }
            label="Require special characters"
          />
        </FormGroup>
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Session & Login Security
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Session Timeout"
          type="number"
          value={securitySettings.sessionTimeout}
          onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: parseInt(e.target.value) })}
          InputProps={{
            endAdornment: <InputAdornment position="end">minutes</InputAdornment>
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Max Failed Login Attempts"
          type="number"
          value={securitySettings.failedLoginAttempts}
          onChange={(e) => setSecuritySettings({ ...securitySettings, failedLoginAttempts: parseInt(e.target.value) })}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Account Lockout Duration"
          type="number"
          value={securitySettings.lockoutDuration}
          onChange={(e) => setSecuritySettings({ ...securitySettings, lockoutDuration: parseInt(e.target.value) })}
          InputProps={{
            endAdornment: <InputAdornment position="end">minutes</InputAdornment>
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={securitySettings.twoFactorEnabled}
              onChange={(e) => setSecuritySettings({ ...securitySettings, twoFactorEnabled: e.target.checked })}
            />
          }
          label="Enable Two-Factor Authentication"
        />
      </Grid>
      
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSaveSettings('security')}
            disabled={saveStatus === 'saving'}
          >
            Save Security Settings
          </Button>
        </Box>
      </Grid>
    </Grid>
  );

  const renderIntegrations = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Connected Integrations
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Integration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Sync</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {integrations.map((integration) => (
                <TableRow key={integration.name}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {integration.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={integration.status} 
                      color={integration.status === 'connected' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {integration.lastSync ? 
                      new Date(integration.lastSync).toLocaleString() : 
                      'Never'
                    }
                  </TableCell>
                  <TableCell>
                    {integration.status === 'connected' ? (
                      <Button size="small" color="error">
                        Disconnect
                      </Button>
                    ) : (
                      <Button size="small" color="primary">
                        Connect
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      
      <Grid item xs={12}>
        <Alert severity="info">
          Additional integrations can be configured through the respective service providers.
        </Alert>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          System Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure your RV park system settings and preferences
        </Typography>
      </Box>

      {saveStatus === 'saved' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}
      {saveStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to save settings. Please try again.
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="General" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Operating Hours" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Payment" icon={<PaymentIcon />} iconPosition="start" />
          <Tab label="Email" icon={<EmailIcon />} iconPosition="start" />
          <Tab label="SMS" icon={<SmsIcon />} iconPosition="start" />
          <Tab label="Security" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="Policies" icon={<PolicyIcon />} iconPosition="start" />
          <Tab label="Integrations" icon={<ApiIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      <Paper>
        <TabPanel value={currentTab} index={0}>
          {renderGeneralSettings()}
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          {renderOperatingHours()}
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          {renderPaymentSettings()}
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Alert severity="info">
            Email settings configuration coming soon!
          </Alert>
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <Alert severity="info">
            SMS settings configuration coming soon!
          </Alert>
        </TabPanel>

        <TabPanel value={currentTab} index={5}>
          {renderSecuritySettings()}
        </TabPanel>

        <TabPanel value={currentTab} index={6}>
          <Alert severity="info">
            Policy configuration coming soon!
          </Alert>
        </TabPanel>

        <TabPanel value={currentTab} index={7}>
          {renderIntegrations()}
        </TabPanel>
      </Paper>
    </Container>
  );
}